import { Extra, ExtraItem } from '@/app/domain/entities/extra';
import { Estimate as OldEstimate } from "@/models/estimate";
import { EstimatedSpace } from "@/models/estimate";
import { unwrapRef } from "@/utils/reactive";
import { CalculationItem } from "@/models/calculate";

import { BaseTransformer, getCalculationItem } from "./base.transformers";
import { Space } from "../entities/space";
import { Partition } from "../entities/partition";
import { PartitionTransformer } from "./partition.transformer";
import { DoorTransformer } from "./door.transformer";
import { Door } from "../entities/door";
import { Overdoor } from "../entities/overdoor";
import { Mounts } from "../entities/mounts";
import { MountsTransformer } from "./mounts.transform";
import { OverdoorTransformer } from "./overdoor.transformer";
import { OverheadItemsTransformer } from "./overheads.transformers";
import { ExtraItemsTransformer } from './extra.transformer';

export const SpaceTransformer: BaseTransformer<EstimatedSpace, Space[]> = {
    toOld(space: Space) {
        const partition = space.getAllPartitions() as unknown as Partition[];

        return {
            code: unwrapRef(space.code),
            title: unwrapRef(space.title),
            structures: partition.map((partition) => {
                const doors = partition.getAllDoors() as unknown as Door[];
                const fillers =
                    partition.getAllOverdoors() as unknown as Overdoor[];
                const mounts = partition.getActiveMount() as unknown as Mounts;
                const extra = partition.getExtra() as unknown as Extra;
                const extraItems = extra ? extra?.getExtraItems() as unknown as ExtraItem[] : [];

                return {
                    code: unwrapRef(partition.code),
                    partition: PartitionTransformer.toOld(partition),
                    doors: doors.map(DoorTransformer.toOld),
                    fillers: fillers.map(OverdoorTransformer.toOld), // transom and fillers
                    mounts: MountsTransformer.toOld(mounts), // CHANEL, TRACKS, PROFILES
                    additionalCosts: [],
                    extra: extraItems.map((item) => ExtraItemsTransformer.toOld(item)),
                };
            }),
        };
    },

    fromOld(
        oldEstimate: OldEstimate,
        calculations: CalculationItem[],
    ): Space[] {
        const spaces = oldEstimate.spaces;
        const oldOverheads = oldEstimate.overhead;
        const systemCode = oldEstimate.info.installedSystemCode;

        const overheads = OverheadItemsTransformer.fromOld(
            oldOverheads,
            calculations,
        );

        return spaces.map((spaceConfig) => {
            const space = new Space({
                code: spaceConfig.code,
                title: spaceConfig.title,
            });

            const spaceCalculations = getCalculationItem(
                calculations,
                spaceConfig.code,
                "space",
            );

            const partitions = spaceConfig.structures.map((partition) =>
                PartitionTransformer.fromOld(
                    partition,
                    spaceCalculations?.subitems.items || [],
                    systemCode
                ),
            );

            space.addManyComponents(partitions);
            space.addComponent(overheads);

            return space;
        });
    },
};
